import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';

// PRD §8 UGC 四件套审核：帖子审核。
// GET /v1/admin/community/posts → 列所有帖子
// POST /v1/admin/community/posts/:id/takedown body{reason} → 下架

type Post = {
  id: string;
  title?: string;
  authorUserId?: string;
  authorId?: string;
  category?: string;
  status?: string; // published / pending_review / blocked / taken_down
  takedownReason?: string;
  createdAt?: string;
};

export default function PostsModerationPage() {
  const [list, setList] = useState<Post[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await api<{ items?: Post[] }>(
        '/admin/community/posts?limit=100',
      );
      setList(r.items ?? []);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const takedown = async (id: string) => {
    const reason = prompt(
      '下架原因（必填，记入 audit log）：',
      '违反社区规范',
    );
    if (!reason) return;
    setBusy(id);
    try {
      await api(`/admin/community/posts/${id}/takedown`, {
        method: 'POST',
        json: { reason },
      });
      await load();
    } catch (e) {
      alert(`下架失败：${(e as ApiError).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={S.header}>
        <h1 style={{ margin: 0 }}>帖子审核</h1>
        <button style={S.refresh} onClick={load}>刷新</button>
      </div>

      {err && <div style={S.err}>加载失败：{err}</div>}
      {list === null && <div style={S.loading}>加载中…</div>}
      {list && list.length === 0 && (
        <div style={S.empty}>暂无帖子。</div>
      )}

      {list && list.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>发布时间</th>
              <th style={S.th}>标题</th>
              <th style={S.th}>作者</th>
              <th style={S.th}>分类</th>
              <th style={S.th}>状态</th>
              <th style={S.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id}>
                <td style={S.td}>{p.createdAt?.slice(0, 16) ?? '-'}</td>
                <td style={S.td}>
                  <code style={{ fontSize: 11, color: '#999' }}>{p.id}</code>
                  <div>{p.title ?? '(无标题)'}</div>
                </td>
                <td style={S.td}>{p.authorUserId ?? p.authorId ?? '-'}</td>
                <td style={S.td}>{p.category ?? '-'}</td>
                <td style={S.td}>
                  <PostStatus status={p.status ?? 'published'} />
                  {p.takedownReason && (
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      {p.takedownReason}
                    </div>
                  )}
                </td>
                <td style={S.td}>
                  {p.status !== 'taken_down' && (
                    <button
                      style={S.btnDanger}
                      disabled={busy === p.id}
                      onClick={() => takedown(p.id)}
                    >
                      {busy === p.id ? '...' : '下架'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PostStatus({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    published: ['可见', '#1f9d8f'],
    pending_review: ['审核中', '#f5a623'],
    blocked: ['机审拦截', '#d33'],
    taken_down: ['已下架', '#888'],
  };
  const [text, color] = map[status] ?? [status, '#888'];
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 4,
        background: `${color}22`,
        color,
        fontSize: 12,
      }}
    >
      {text}
    </span>
  );
}

const S: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  refresh: {
    padding: '6px 14px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: 6,
    cursor: 'pointer',
  },
  err: {
    padding: 12,
    background: '#fee',
    color: '#d33',
    borderRadius: 6,
    marginBottom: 16,
  },
  loading: { color: '#888', padding: 24 },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#888',
    background: 'white',
    borderRadius: 8,
  },
  table: {
    width: '100%',
    background: 'white',
    borderRadius: 8,
    borderCollapse: 'collapse',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: 12,
    color: '#666',
    background: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    borderBottom: '1px solid #f4f4f4',
    verticalAlign: 'top',
  },
  btnDanger: {
    padding: '4px 12px',
    background: 'white',
    color: '#d33',
    border: '1px solid #d33',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
  },
};
